import type { EdgeBucketingDef } from './flag-types';

/**
 * The bucketing inputs for every edge experiment — and the ONLY flags module
 * the CloudFront viewer-request bundle imports.
 *
 * Everything in this file ships to a ≤3,674-byte function
 * (`packages/infra/src/web/edge.ts`), so it holds the four fields
 * `handler-request.ts` actually reads and nothing else. `EdgeClientFlags` in
 * `./edge-client-flags` composes the full `FeatureFlagDef` — `variants`,
 * `default`, `bucketing.mode` — on top of these entries for every non-edge
 * consumer, so there is one source of truth and no duplicated split.
 *
 * The isolation is physical, not conventional: rolldown cannot tree-shake
 * object properties and `@listening/frontend` is not marked
 * `sideEffects: false`, so a field written here is a field the edge pays for
 * whether or not it is read. Same reason `edge-client-flags.ts` was split out
 * of `client-flags.ts`, and `edge-experiment-policies.ts` out of both.
 *
 * **Import NOTHING here.** A value import from another module drags that
 * module's own graph into the bundle. Splits are written as literals for
 * exactly that reason, including `pricingTestV1`'s, which is pinned against
 * `@listening/api-contract`'s copy by `test/bucketing/pricing-split-parity.test.ts`
 * rather than imported from it.
 *
 * Re-measure with `bun run build:edge:request` + `wc -c` in `apps/start` before
 * merging any change to this file.
 */
export const EdgeBucketing = {
  academicLandingFunnelV1: {
    key: 'academic-landing-funnel-v1',
    enrollment: 1,
    split: { control: 0.5, treatment: 0.5 },
    fallbackVariant: 'control',
  },
  homepageCtaFunnelV1: {
    key: 'homepage-cta-funnel-v1',
    enrollment: 1,
    split: { control: 0.5, treatment: 0.5 },
    fallbackVariant: 'control',
  },
  /**
   * Six-leg annual price × free-trial test, Mode C: the edge cookie assigns the
   * arm, and signup freezes it onto the account
   * (`edge-experiment-policies.ts`, `pinning: 'at-signup'`).
   *
   * **The variants are edge CODES, not arm keys.** `a`–`f` map to `control`
   * … `price-79-trial` through `PRICING_TEST_V1_EDGE_CODES` in
   * `@listening/api-contract/pricing-experiment`, which is the only sanctioned
   * way to cross that boundary. The arm keys are 185 bytes this function does
   * not have; see that constant's docstring for the measurement and for which
   * strings are canonical where (Stripe and PostHog carry arm keys, always).
   *
   * `enrollment: 1` — the experiment decides a price, and the visitor's own
   * `/pricing` page must be able to show it. A partial enrollment would put a
   * cookie-less-looking non-enrolled state on a CDN-cached marketing surface
   * for no statistical gain at this traffic level.
   *
   * `c` and `f` (the two $79 arms) are RETIRED at weight 0: they stay in the
   * split because the codes are pinned on accounts and must keep decoding, but
   * no visitor hashes into them, and a pinned account resolves as a non-subject
   * to whichever arm absorbed its hash range (`ExperimentAssignments.resolveArm`
   * via `absorbingVariant`: `c` → `b`, `f` → `e`, so the page and the paywall
   * agree on the trial too). Each retired share
   * was folded into the arm before it (`b` took `c`'s, `e` took `f`'s) so the
   * surviving cumulative boundaries — 0.17, 0.51, 0.68, 1 — are the launch
   * ones bit for bit and no `a`/`b`/`d`/`e` cookie re-rolls; only old `c`/`f`
   * cookies move, onto the $59 arms. See `PRICING_TEST_V1_SPLIT` for why
   * equal quarters were rejected. The sum is exactly 1, so the running total
   * reaches 1 at `e` and the retired tail `f` can never catch `pickVariant`'s
   * past-the-sum remainder.
   *
   * `fallbackVariant: 'b'` is `price-59-no-trial`, the TOP-priced LIVE arm —
   * decision C4. Crawlers and cookie-less requests are never advertised a price
   * below what any assignable arm charges, so a bot-detection miss cannot index
   * a page that under-quotes the visitor's real checkout.
   *
   * Split matches `PRICING_TEST_V1_SPLIT` exactly (parity-tested) so the
   * PostHog analysis and the edge assignment apportion the same shares.
   */
  pricingTestV1: {
    key: 'pricing-test-v1',
    enrollment: 1,
    split: { a: 0.17, b: 0.34, c: 0, d: 0.17, e: 0.32, f: 0 },
    fallbackVariant: 'b',
  },
} as const satisfies Record<string, EdgeBucketingDef>;
